import moment from 'moment';

// Countries that use DD/MM/YYYY format
const DD_MM_YYYY_COUNTRIES = [
  'GB', 'AU', 'IN', 'BR', 'FR', 'DE', 'IT', 'ES', 'NL', 'BE', 'DK', 'SE',
  'NO', 'FI', 'IE', 'AT', 'PT', 'GR', 'PL', 'CZ', 'HU', 'SK', 'SI', 'HR',
  'BG', 'RO', 'LT', 'LV', 'EE', 'MT', 'CY', 'LU', 'AR', 'CL', 'UY', 'PY',
  'BO', 'PE', 'EC', 'CO', 'VE', 'NZ', 'ZA', 'EG', 'SA', 'AE', 'QA', 'KW',
  'BH', 'OM', 'JO', 'LB', 'IQ', 'SY', 'IL', 'PS', 'TR', 'RU', 'UA', 'BY',
  'KZ', 'UZ', 'KG', 'TJ', 'TM', 'MD', 'GE', 'AM', 'AZ', 'CN', 'JP', 'KR',
  'TH', 'VN', 'MY', 'SG', 'PH', 'ID', 'TW', 'HK', 'MO', 'CA'
];

export const shouldUseDDMMFormat = (countryCode) => {
  return DD_MM_YYYY_COUNTRIES.includes(countryCode?.toUpperCase());
};

export const getDisplayFormat = (countryCode) => {
  return shouldUseDDMMFormat(countryCode) ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
};

export const getMomentDisplayFormat = (countryCode) => {
  return shouldUseDDMMFormat(countryCode) ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
};

export const getMomentDisplayFormatShort = (countryCode) => {
  return shouldUseDDMMFormat(countryCode) ? 'DD/MM/YY' : 'MM/DD/YY';
};

export const getInputMask = () => {
  return '11/11/1111'; // Same mask, different interpretation
};

export const getInputPlaceholder = (countryCode) => {
  return getDisplayFormat(countryCode);
};

// Convert backend date (MM/DD/YYYY or YYYY-MM-DD or moment) to user's display format
export const formatDateForUser = (backendDate, countryCode) => {
  if (!backendDate) return '';

  let momentDate;

  // Handle moment objects
  if (moment.isMoment(backendDate)) {
    momentDate = backendDate;
  } else if (typeof backendDate === 'string' && backendDate.includes('-')) {
    // Handle ISO format (YYYY-MM-DD)
    momentDate = moment(backendDate, 'YYYY-MM-DD');
  } else if (typeof backendDate === 'string' && backendDate.includes('/')) {
    // Handle MM/DD/YYYY format
    momentDate = moment(backendDate, 'MM/DD/YYYY');
  } else {
    // Handle other date formats
    momentDate = moment(backendDate);
  }

  if (!momentDate.isValid()) {
    // Fallback to original value, but never return a moment object
    return moment.isMoment(backendDate) ? '' : backendDate;
  }

  const userFormat = getMomentDisplayFormat(countryCode);
  return momentDate.format(userFormat);
};

// Format a from/to date pair for the user: one date when both ends match, a range otherwise
export const formatDateRangeForUser = (fromDate, toDate, countryCode) => {
  const from = formatDateForUser(fromDate, countryCode);
  const to = formatDateForUser(toDate, countryCode);
  if (!from) return to;
  if (!to || to === from) return from;
  return from + ' - ' + to;
};

// Convert backend date to user's short display format (DD/MM/YY or MM/DD/YY)
export const formatDateForUserShort = (backendDate, countryCode) => {
  if (!backendDate) return '';

  let momentDate;

  // Handle moment objects
  if (moment.isMoment(backendDate)) {
    momentDate = backendDate;
  } else if (typeof backendDate === 'string' && backendDate.includes('-')) {
    // Handle ISO format (YYYY-MM-DD)
    momentDate = moment(backendDate, 'YYYY-MM-DD');
  } else if (typeof backendDate === 'string' && backendDate.includes('/')) {
    // Handle MM/DD/YYYY format
    momentDate = moment(backendDate, 'MM/DD/YYYY');
  } else {
    // Handle other date formats
    momentDate = moment(backendDate);
  }

  if (!momentDate.isValid()) {
    // Fallback to original value, but never return a moment object
    return moment.isMoment(backendDate) ? '' : backendDate;
  }

  const userFormat = getMomentDisplayFormatShort(countryCode);
  return momentDate.format(userFormat);
};

// Convert user input to backend expected format (MM/DD/YYYY)
export const formatDateForBackend = (userDate, countryCode) => {
  if (!userDate) return '';

  // Check if input is in ISO format (YYYY-MM-DD) from mobile date picker
  if (typeof userDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(userDate)) {
    const isoDate = moment(userDate, 'YYYY-MM-DD', true);
    if (isoDate.isValid()) {
      // Convert ISO format to MM/DD/YYYY for backend
      return isoDate.format('MM/DD/YYYY');
    }
  }

  const userFormat = getMomentDisplayFormat(countryCode);
  const momentDate = moment(userDate, userFormat, true);

  if (!momentDate.isValid()) {
    // Try to parse with US format as fallback
    const fallbackDate = moment(userDate, 'MM/DD/YYYY', true);
    return fallbackDate.isValid() ? userDate : '';
  }

  // Always return in MM/DD/YYYY format for backend
  return momentDate.format('MM/DD/YYYY');
};

// Validate user date input
export const validateUserDateInput = (userDate, countryCode) => {
  if (!userDate) return { isValid: true, error: null };

  const userFormat = getMomentDisplayFormat(countryCode);
  const momentDate = moment(userDate, userFormat, true);

  if (!momentDate.isValid()) {
    return {
      isValid: false,
      error: `Invalid date format. Please use ${userFormat}`
    };
  }

  return { isValid: true, error: null };
};

// Format date for display with time
export const formatDateTimeForUser = (backendDateTime, countryCode) => {
  if (!backendDateTime) return '';

  const momentDate = moment(backendDateTime);
  if (!momentDate.isValid()) return backendDateTime;

  const dateFormat = getMomentDisplayFormat(countryCode);
  return momentDate.format(`${dateFormat}, hh:mm A`);
};
